import { useEffect, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const navItems = [
  { label: "首页", to: "/" },
  { label: "电影", to: "/category/movie" },
  { label: "电视剧", to: "/category/tv" },
  { label: "综艺", to: "/category/variety" },
  { label: "资讯", to: "/news" },
  { label: "专题", to: "/topic/topic1" },
];

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [keyword, setKeyword] = useState("");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
  }, [location.pathname, location.search]);

  const isActive = (to: string) => {
    if (to === "/") return location.pathname === "/";
    return (
      location.pathname === to ||
      location.pathname.startsWith(`${to}/`) ||
      (to === "/news" && location.pathname.startsWith("/news")) ||
      (to === "/topic/topic1" && location.pathname.startsWith("/topic"))
    );
  };

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    const q = keyword.trim();
    if (!q) return;
    navigate(`/search?q=${encodeURIComponent(q)}`);
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-colors duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur border-b border-background-200/70"
          : "bg-background-50/80 backdrop-blur"
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex h-16 items-center gap-4">
          <Link
            to="/"
            className="flex items-center gap-2 cursor-pointer shrink-0"
            aria-label="po18 首页"
          >
            <span className="w-9 h-9 rounded-md bg-primary-500 text-background-50 flex items-center justify-center text-sm font-black tracking-tight">
              p18
            </span>
            <span className="text-xl font-black tracking-tight text-foreground-950">
              po<span className="text-primary-500">18</span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1 ml-4">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  isActive(item.to)
                    ? "text-primary-600 bg-primary-50"
                    : "text-foreground-700 hover:text-primary-600 hover:bg-background-100"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="flex-1" />

          <form
            onSubmit={submitSearch}
            className="hidden lg:flex items-center gap-2"
          >
            <div className="w-56 xl:w-64 h-10 flex items-center gap-2 px-3 rounded-full bg-background-100 border border-background-200 focus-within:border-primary-300 transition-colors">
              <span className="w-5 h-5 flex items-center justify-center text-foreground-500">
                <i className="ri-search-line text-base" />
              </span>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索影视、资讯…"
                className="flex-1 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 outline-none"
              />
            </div>
            <button
              type="submit"
              className="px-4 h-10 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              搜索
            </button>
          </form>

          <button
            type="button"
            onClick={() => setSearchOpen((v) => !v)}
            className="lg:hidden w-10 h-10 flex items-center justify-center rounded-full text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
            aria-label="搜索"
          >
            <i className="ri-search-line text-lg" />
          </button>

          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-full text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer"
            aria-label="菜单"
          >
            <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"} />
          </button>
        </div>

        {searchOpen && (
          <form
            onSubmit={submitSearch}
            className="lg:hidden pb-3 flex items-center gap-2"
          >
            <div className="flex-1 h-10 flex items-center gap-2 px-3 rounded-full bg-background-100 border border-background-200 focus-within:border-primary-300 transition-colors">
              <span className="w-5 h-5 flex items-center justify-center text-foreground-500">
                <i className="ri-search-line text-base" />
              </span>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索影视、资讯…"
                className="flex-1 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 outline-none"
              />
            </div>
            <button
              type="submit"
              className="px-4 h-10 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              搜索
            </button>
          </form>
        )}
      </div>

      {menuOpen && (
        <nav className="md:hidden border-t border-background-200/70 bg-background-50 px-4 py-2">
          {navItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`block px-3 py-3 rounded-md text-sm font-medium transition-colors cursor-pointer ${
                isActive(item.to)
                  ? "text-primary-600 bg-primary-50"
                  : "text-foreground-700 hover:bg-background-100"
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>
      )}
    </header>
  );
}